import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Archive,
  ArrowRight,
  Award,
  Bell,
  BookOpen,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock3,
  Download,
  Dumbbell,
  Eye,
  EyeOff,
  Flame,
  Home,
  ListTodo,
  LockKeyhole,
  LogOut,
  Mail,
  MapPinned,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Star,
  Target,
  Trash2,
  Trophy,
  UserRound,
  UsersRound,
  X,
  Zap,
} from 'lucide-react';

const DATA_KEY = 'questup:data:v2';
const SESSION_KEY = 'questup:session:v2';
const XP_PER_LEVEL = 600;
const VALID_ROUTES = ['/login', '/dashboard', '/quests', '/achievements', '/profile'];

const CATEGORY_META = {
  story: { label: 'เนื้อเรื่อง', icon: BookOpen, color: 'blue' },
  daily: { label: 'รายวัน', icon: CalendarDays, color: 'cyan' },
  training: { label: 'ฝึกฝน', icon: Dumbbell, color: 'amber' },
  community: { label: 'ชุมชน', icon: UsersRound, color: 'green' },
};

const DIFFICULTY_META = {
  easy: { label: 'ง่าย', xp: 60, color: 'green' },
  medium: { label: 'กลาง', xp: 120, color: 'amber' },
  hard: { label: 'ยาก', xp: 240, color: 'red' },
};

const PAGE_META = {
  '/dashboard': {
    eyebrow: 'COMMAND CENTER',
    title: 'ภาพรวมฮีโร่',
    description: 'ทุกภารกิจ ทุกแต้ม XP อยู่ในสายตาเดียว',
  },
  '/quests': {
    eyebrow: 'MISSION BOARD',
    title: 'ภารกิจทั้งหมด',
    description: 'เลือกเป้าหมาย แล้วลงมือช่วยโลกทีละภารกิจ',
  },
  '/achievements': {
    eyebrow: 'TROPHY VAULT',
    title: 'ความสำเร็จ',
    description: 'ทุกก้าวเล็ก ๆ กำลังสร้างตำนานของคุณ',
  },
  '/profile': {
    eyebrow: 'HERO IDENTITY',
    title: 'โปรไฟล์ผู้เล่น',
    description: 'ข้อมูลความก้าวหน้าและการจัดการบัญชีเดโม',
  },
};

const NAV_ITEMS = [
  { path: '/dashboard', label: 'ภาพรวม', icon: Home },
  { path: '/quests', label: 'ภารกิจ', icon: ListTodo },
  { path: '/achievements', label: 'รางวัล', icon: Trophy },
  { path: '/profile', label: 'โปรไฟล์', icon: UserRound },
];

function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateKeyOffset(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateKey(date);
}

function isoOffset(hours) {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

function createSeedData() {
  return {
    version: 2,
    profile: {
      id: 'demo-web-hero',
      name: 'Miles Quest',
      email: 'demo@questup.app',
      joinedAt: '2026-01-12T08:30:00.000Z',
    },
    progress: {
      totalXp: 2460,
      completedCount: 18,
      currentStreak: 12,
      bestStreak: 16,
      lastCompletionDate: localDateKey(),
    },
    quests: [
      {
        id: 'quest-story-signal',
        title: 'ตามหาสัญญาณปริศนาในย่านเก่า',
        description: 'แบ่งงานค้นคว้าใหญ่ให้เป็น 4 จุดตรวจ แล้วปิดภารกิจสำคัญของสัปดาห์ให้ได้',
        category: 'story',
        difficulty: 'hard',
        xpReward: 240,
        dueDate: dateKeyOffset(1),
        status: 'active',
        progress: 3,
        total: 4,
        featured: true,
        createdAt: isoOffset(-72),
        completedAt: null,
      },
      {
        id: 'quest-daily-focus',
        title: 'โฟกัสงานสำคัญ 45 นาที',
        description: 'ปิดการแจ้งเตือน วางโทรศัพท์ และทำงานชิ้นเดียวให้เดินหน้าอย่างชัดเจน',
        category: 'daily',
        difficulty: 'medium',
        xpReward: 120,
        dueDate: localDateKey(),
        status: 'active',
        progress: 0,
        total: 1,
        featured: false,
        createdAt: isoOffset(-12),
        completedAt: null,
      },
      {
        id: 'quest-training-sense',
        title: 'ฝึก Spider-Sense ก่อนเริ่มวัน',
        description: 'ทบทวนเป้าหมาย 3 ข้อและจัดลำดับสิ่งสำคัญก่อนเปิดกล่องข้อความ',
        category: 'training',
        difficulty: 'easy',
        xpReward: 60,
        dueDate: localDateKey(),
        status: 'active',
        progress: 2,
        total: 3,
        featured: false,
        createdAt: isoOffset(-8),
        completedAt: null,
      },
      {
        id: 'quest-community-call',
        title: 'โทรหาคนที่ไม่ได้คุยนาน',
        description: 'ใช้เวลาเล็กน้อยเชื่อมต่อกับคนสำคัญ เพราะฮีโร่ไม่จำเป็นต้องลุยคนเดียว',
        category: 'community',
        difficulty: 'easy',
        xpReward: 60,
        dueDate: dateKeyOffset(2),
        status: 'active',
        progress: 0,
        total: 1,
        featured: false,
        createdAt: isoOffset(-5),
        completedAt: null,
      },
      {
        id: 'quest-complete-workout',
        title: 'ออกกำลังกาย 20 นาที',
        description: 'ขยับร่างกายให้พร้อมรับมือภารกิจถัดไป',
        category: 'training',
        difficulty: 'medium',
        xpReward: 120,
        dueDate: dateKeyOffset(-1),
        status: 'completed',
        progress: 1,
        total: 1,
        featured: false,
        createdAt: isoOffset(-36),
        completedAt: isoOffset(-20),
      },
      {
        id: 'quest-complete-plan',
        title: 'วางแผนภารกิจประจำสัปดาห์',
        description: 'เคลียร์หัวให้โล่งและเลือกชัยชนะที่สำคัญที่สุด',
        category: 'daily',
        difficulty: 'easy',
        xpReward: 60,
        dueDate: dateKeyOffset(-2),
        status: 'completed',
        progress: 1,
        total: 1,
        featured: false,
        createdAt: isoOffset(-60),
        completedAt: isoOffset(-48),
      },
    ],
    achievementUnlocks: {
      'first-thread': isoOffset(-720),
      'on-fire': isoOffset(-120),
    },
    activity: [
      {
        id: 'activity-seed-1',
        type: 'quest_completed',
        title: 'ออกกำลังกาย 20 นาที',
        xpDelta: 120,
        createdAt: isoOffset(-20),
      },
      {
        id: 'activity-seed-2',
        type: 'streak',
        title: 'รักษาสตรีกครบ 12 วัน',
        xpDelta: 0,
        createdAt: isoOffset(-24),
      },
      {
        id: 'activity-seed-3',
        type: 'quest_completed',
        title: 'วางแผนภารกิจประจำสัปดาห์',
        xpDelta: 60,
        createdAt: isoOffset(-48),
      },
    ],
  };
}

const ACHIEVEMENTS = [
  {
    id: 'first-thread',
    title: 'เส้นใยเส้นแรก',
    description: 'ทำภารกิจแรกสำเร็จ',
    target: 1,
    value: (data) => data.progress.completedCount,
    icon: Sparkles,
    tone: 'red',
  },
  {
    id: 'on-fire',
    title: 'ไฟไม่เคยดับ',
    description: 'รักษาสตรีกให้ถึง 7 วัน',
    target: 7,
    value: (data) => data.progress.currentStreak,
    icon: Flame,
    tone: 'amber',
  },
  {
    id: 'web-warrior',
    title: 'Web Warrior',
    description: 'สะสมพลังให้ครบ 3,000 XP',
    target: 3000,
    value: (data) => data.progress.totalXp,
    icon: Shield,
    tone: 'blue',
  },
  {
    id: 'neighborhood-hero',
    title: 'ฮีโร่ข้างบ้าน',
    description: 'ทำภารกิจสำเร็จ 25 ครั้ง',
    target: 25,
    value: (data) => data.progress.completedCount,
    icon: Award,
    tone: 'green',
  },
  {
    id: 'unstoppable',
    title: 'ไม่มีอะไรมาหยุดได้',
    description: 'รักษาสตรีกให้ถึง 30 วัน',
    target: 30,
    value: (data) => data.progress.bestStreak,
    icon: Zap,
    tone: 'violet',
  },
  {
    id: 'legend',
    title: 'ตำนานแห่งเมือง',
    description: 'ทำภารกิจสำเร็จ 50 ครั้ง',
    target: 50,
    value: (data) => data.progress.completedCount,
    icon: Trophy,
    tone: 'gold',
  },
];

function loadInitialData() {
  try {
    const raw = localStorage.getItem(DATA_KEY);
    if (!raw) return { data: createSeedData(), recovered: false };
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 2 || !Array.isArray(parsed.quests) || !parsed.progress) {
      throw new Error('Unsupported data shape');
    }
    return { data: parsed, recovered: false };
  } catch {
    return { data: createSeedData(), recovered: true };
  }
}

function readSession() {
  try {
    return Boolean(JSON.parse(localStorage.getItem(SESSION_KEY))?.authenticated);
  } catch {
    return false;
  }
}

function getLevel(totalXp) {
  return Math.floor(totalXp / XP_PER_LEVEL) + 1;
}

function getLevelProgress(totalXp) {
  return totalXp % XP_PER_LEVEL;
}

function formatDate(dateString, options = { day: 'numeric', month: 'short' }) {
  if (!dateString) return 'ไม่กำหนด';
  return new Intl.DateTimeFormat('th-TH', options).format(new Date(dateString));
}

function formatRelativeTime(isoString) {
  const diffMinutes = Math.max(0, Math.round((Date.now() - new Date(isoString).getTime()) / 60000));
  if (diffMinutes < 1) return 'เมื่อสักครู่';
  if (diffMinutes < 60) return `${diffMinutes} นาทีที่แล้ว`;
  const hours = Math.round(diffMinutes / 60);
  if (hours < 24) return `${hours} ชั่วโมงที่แล้ว`;
  return `${Math.round(hours / 24)} วันที่แล้ว`;
}

function dayDifference(fromKey, toKey) {
  if (!fromKey) return null;
  const from = new Date(`${fromKey}T00:00:00`);
  const to = new Date(`${toKey}T00:00:00`);
  return Math.round((to - from) / 86400000);
}

function SpiderMark({ size = 30, className = '' }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <ellipse cx="12" cy="12.4" rx="3.1" ry="4.8" />
      <path d="M10.4 8.2 8 5.6M13.6 8.2 16 5.6M9.2 10.1 5.5 8.4M14.8 10.1l3.7-1.7M8.9 12.8 4.7 13M15.1 12.8l4.2.2M9.5 15.3l-3.3 3M14.5 15.3l3.3 3" />
      <path d="M10.7 7.8c.2-1.7.7-2.6 1.3-2.6s1.1.9 1.3 2.6" />
    </svg>
  );
}

function Logo({ compact = false }) {
  return (
    <div className={`brand-lockup ${compact ? 'is-compact' : ''}`}>
      <span className="brand-mark"><SpiderMark size={compact ? 23 : 28} /></span>
      <span className="brand-type">
        <strong>QUEST<span>UP</span></strong>
        {!compact && <small>WEB HERO EDITION</small>}
      </span>
    </div>
  );
}

function ProgressBar({ value, max, tone = 'red', label }) {
  const percentage = Math.min(100, Math.max(0, (value / Math.max(max, 1)) * 100));
  return (
    <div className="progress-wrap" aria-label={label}>
      <div className={`progress-track tone-${tone}`}>
        <span style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

function App() {
  const initialLoad = useMemo(loadInitialData, []);
  const [data, setData] = useState(initialLoad.data);
  const [isAuthenticated, setIsAuthenticated] = useState(readSession);
  const [route, setRoute] = useState(window.location.pathname || '/');
  const [toast, setToast] = useState(null);
  const [questModal, setQuestModal] = useState(null);
  const [detailQuestId, setDetailQuestId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [undoSnapshot, setUndoSnapshot] = useState(null);

  const navigate = useCallback((nextRoute, options = {}) => {
    if (window.location.pathname !== nextRoute) {
      window.history[options.replace ? 'replaceState' : 'pushState']({}, '', nextRoute);
    }
    setRoute(nextRoute);
    window.scrollTo({ top: 0, behavior: options.instant ? 'auto' : 'smooth' });
  }, []);

  useEffect(() => {
    const handlePopState = () => setRoute(window.location.pathname || '/');
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === DATA_KEY && event.newValue) {
        try {
          setData(JSON.parse(event.newValue));
        } catch {
          // Another tab may be resetting data; keep the current safe state.
        }
      }
      if (event.key === SESSION_KEY) setIsAuthenticated(readSession());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    localStorage.setItem(DATA_KEY, JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    const knownRoute = VALID_ROUTES.includes(route);
    if (!isAuthenticated && route !== '/login') {
      navigate('/login', { replace: true, instant: true });
    } else if (isAuthenticated && (route === '/' || route === '/login')) {
      navigate('/dashboard', { replace: true, instant: true });
    } else if (!knownRoute && !isAuthenticated) {
      navigate('/login', { replace: true, instant: true });
    }
  }, [isAuthenticated, navigate, route]);

  useEffect(() => {
    if (initialLoad.recovered && isAuthenticated) {
      setToast({ type: 'warning', message: 'กู้ข้อมูลเดโมกลับเป็นค่าเริ่มต้นแล้ว เพราะข้อมูลเดิมอ่านไม่ได้' });
    }
  }, [initialLoad.recovered, isAuthenticated]);

  const showToast = useCallback((message, type = 'success', action = null) => {
    setToast({ message, type, action, id: Date.now() });
  }, []);

  const login = useCallback(() => {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ authenticated: true, email: 'demo@questup.app', signedInAt: new Date().toISOString() }),
    );
    setIsAuthenticated(true);
    navigate('/dashboard', { replace: true });
    showToast('ยินดีต้อนรับกลับ ฮีโร่! ภารกิจพร้อมแล้ว');
  }, [navigate, showToast]);

  const logout = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
    setQuestModal(null);
    setDetailQuestId(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const saveQuest = useCallback((formValues, editingId) => {
    const now = new Date().toISOString();
    const difficulty = DIFFICULTY_META[formValues.difficulty];
    if (editingId) {
      setData((current) => ({
        ...current,
        quests: current.quests.map((quest) =>
          quest.id === editingId
            ? {
                ...quest,
                title: formValues.title.trim(),
                description: formValues.description.trim(),
                category: formValues.category,
                difficulty: formValues.difficulty,
                xpReward: difficulty.xp,
                dueDate: formValues.dueDate || null,
              }
            : quest,
        ),
      }));
      showToast('อัปเดตภารกิจเรียบร้อย');
    } else {
      const newQuest = {
        id: crypto.randomUUID(),
        title: formValues.title.trim(),
        description: formValues.description.trim(),
        category: formValues.category,
        difficulty: formValues.difficulty,
        xpReward: difficulty.xp,
        dueDate: formValues.dueDate || null,
        status: 'active',
        progress: 0,
        total: 1,
        featured: false,
        createdAt: now,
        completedAt: null,
      };
      setData((current) => ({
        ...current,
        quests: [newQuest, ...current.quests],
        activity: [
          { id: crypto.randomUUID(), type: 'quest_created', title: newQuest.title, xpDelta: 0, createdAt: now },
          ...current.activity,
        ].slice(0, 100),
      }));
      showToast('เพิ่มภารกิจใหม่ลงบอร์ดแล้ว');
    }
    setQuestModal(null);
  }, [showToast]);

  const completeQuest = useCallback((questId) => {
    const quest = data.quests.find((item) => item.id === questId);
    if (!quest || quest.status !== 'active') return;

    const previous = structuredClone(data);
    const now = new Date().toISOString();
    const today = localDateKey();
    const daysSince = dayDifference(data.progress.lastCompletionDate, today);
    const nextStreak =
      daysSince === 0
        ? data.progress.currentStreak
        : daysSince === 1
          ? data.progress.currentStreak + 1
          : 1;

    let nextData = {
      ...data,
      quests: data.quests.map((item) =>
        item.id === questId
          ? { ...item, status: 'completed', progress: item.total, completedAt: now }
          : item,
      ),
      progress: {
        ...data.progress,
        totalXp: data.progress.totalXp + quest.xpReward,
        completedCount: data.progress.completedCount + 1,
        currentStreak: nextStreak,
        bestStreak: Math.max(data.progress.bestStreak, nextStreak),
        lastCompletionDate: today,
      },
      activity: [
        {
          id: crypto.randomUUID(),
          type: 'quest_completed',
          title: quest.title,
          xpDelta: quest.xpReward,
          createdAt: now,
        },
        ...data.activity,
      ].slice(0, 100),
    };

    const newlyUnlocked = ACHIEVEMENTS.filter(
      (achievement) =>
        !nextData.achievementUnlocks[achievement.id] && achievement.value(nextData) >= achievement.target,
    );

    if (newlyUnlocked.length) {
      const unlocks = { ...nextData.achievementUnlocks };
      const unlockActivities = newlyUnlocked.map((achievement) => {
        unlocks[achievement.id] = now;
        return {
          id: crypto.randomUUID(),
          type: 'achievement',
          title: achievement.title,
          xpDelta: 0,
          createdAt: now,
        };
      });
      nextData = {
        ...nextData,
        achievementUnlocks: unlocks,
        activity: [...unlockActivities, ...nextData.activity].slice(0, 100),
      };
    }

    setUndoSnapshot(previous);
    setData(nextData);
    setDetailQuestId(null);
    const unlockText = newlyUnlocked.length ? ` · ปลดล็อก ${newlyUnlocked[0].title}` : '';
    showToast(`ภารกิจสำเร็จ +${quest.xpReward} XP${unlockText}`, 'success', 'undo');
  }, [data, showToast]);

  const undoCompletion = useCallback(() => {
    if (!undoSnapshot) return;
    setData(undoSnapshot);
    setUndoSnapshot(null);
    setToast(null);
    showToast('ย้อนคืนการทำภารกิจแล้ว', 'info');
  }, [showToast, undoSnapshot]);

  const archiveQuest = useCallback((questId) => {
    setData((current) => ({
      ...current,
      quests: current.quests.map((quest) =>
        quest.id === questId ? { ...quest, status: 'archived' } : quest,
      ),
    }));
    setDetailQuestId(null);
    showToast('ย้ายภารกิจไปคลังแล้ว', 'info');
  }, [showToast]);

  const requestDeleteQuest = useCallback((questId) => {
    const quest = data.quests.find((item) => item.id === questId);
    if (!quest) return;
    setConfirmDialog({ type: 'delete', questId, title: quest.title });
  }, [data.quests]);

  const confirmAction = useCallback(() => {
    if (confirmDialog?.type === 'delete') {
      setData((current) => ({
        ...current,
        quests: current.quests.filter((quest) => quest.id !== confirmDialog.questId),
      }));
      setDetailQuestId(null);
      showToast('ลบภารกิจออกแล้ว', 'info');
    }
    if (confirmDialog?.type === 'reset') {
      setData(createSeedData());
      setDetailQuestId(null);
      showToast('รีเซ็ตข้อมูลเดโมกลับจุดเริ่มต้นแล้ว', 'info');
      navigate('/dashboard');
    }
    setConfirmDialog(null);
  }, [confirmDialog, navigate, showToast]);

  const exportData = useCallback(() => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `questup-backup-${localDateKey()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    showToast('ดาวน์โหลดข้อมูลสำรองแล้ว', 'success');
  }, [data, showToast]);

  const detailQuest = detailQuestId ? data.quests.find((quest) => quest.id === detailQuestId) : null;

  if (!isAuthenticated) {
    return <LoginScreen onLogin={login} />;
  }

  const renderPage = () => {
    if (!VALID_ROUTES.includes(route)) {
      return <NotFoundPage onNavigate={navigate} />;
    }
    if (route === '/quests') {
      return (
        <QuestsPage
          data={data}
          onAdd={() => setQuestModal({ mode: 'create' })}
          onEdit={(quest) => setQuestModal({ mode: 'edit', quest })}
          onComplete={completeQuest}
          onArchive={archiveQuest}
          onDelete={requestDeleteQuest}
          onOpen={setDetailQuestId}
        />
      );
    }
    if (route === '/achievements') return <AchievementsPage data={data} />;
    if (route === '/profile') {
      return (
        <ProfilePage
          data={data}
          onReset={() => setConfirmDialog({ type: 'reset' })}
          onExport={exportData}
          onLogout={logout}
        />
      );
    }
    return (
      <DashboardPage
        data={data}
        onNavigate={navigate}
        onAdd={() => setQuestModal({ mode: 'create' })}
        onComplete={completeQuest}
        onOpen={setDetailQuestId}
      />
    );
  };

  return (
    <>
      <AppShell
        route={route}
        data={data}
        onNavigate={navigate}
        onAdd={() => setQuestModal({ mode: 'create' })}
        onNotify={showToast}
      >
        {renderPage()}
      </AppShell>

      {questModal && (
        <QuestEditorModal
          quest={questModal.mode === 'edit' ? questModal.quest : null}
          onClose={() => setQuestModal(null)}
          onSave={saveQuest}
        />
      )}

      {detailQuest && (
        <QuestDetailModal
          quest={detailQuest}
          onClose={() => setDetailQuestId(null)}
          onEdit={(quest) => {
            setDetailQuestId(null);
            setQuestModal({ mode: 'edit', quest });
          }}
          onComplete={completeQuest}
          onArchive={archiveQuest}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.type === 'reset' ? 'รีเซ็ตข้อมูลเดโม?' : 'ลบภารกิจนี้?'}
          description={
            confirmDialog.type === 'reset'
              ? 'ภารกิจ ความก้าวหน้า และกิจกรรมทั้งหมดจะกลับเป็นข้อมูลเริ่มต้น การกระทำนี้ย้อนกลับไม่ได้'
              : `“${confirmDialog.title}” จะถูกลบจากบอร์ดอย่างถาวร`
          }
          confirmLabel={confirmDialog.type === 'reset' ? 'รีเซ็ตข้อมูล' : 'ลบภารกิจ'}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmAction}
        />
      )}

      {toast && (
        <Toast
          key={toast.id || toast.message}
          toast={toast}
          onClose={() => setToast(null)}
          onUndo={undoCompletion}
        />
      )}
    </>
  );
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const authenticate = (nextEmail, nextPassword) => {
    setError('');
    setLoading(true);
    window.setTimeout(() => {
      if (nextEmail.trim().toLowerCase() === 'demo@questup.app' && nextPassword === 'questup123') {
        onLogin();
      } else {
        setLoading(false);
        setError('อีเมลหรือรหัสผ่านไม่ถูกต้อง ลองใช้บัญชีเดโมด้านล่าง');
      }
    }, 650);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError('กรุณากรอกอีเมลและรหัสผ่านให้ครบ');
      return;
    }
    authenticate(email, password);
  };

  const enterDemo = () => {
    setEmail('demo@questup.app');
    setPassword('questup123');
    authenticate('demo@questup.app', 'questup123');
  };

  return (
    <main className="login-page">
      <div className="login-web web-one" aria-hidden="true" />
      <div className="login-web web-two" aria-hidden="true" />
      <section className="login-story" aria-label="QuestUp introduction">
        <Logo />
        <div className="login-story-copy">
          <span className="comic-kicker"><SpiderMark size={19} /> FRIENDLY NEIGHBORHOOD PRODUCTIVITY</span>
          <h1>ภารกิจของวันนี้<br /><em>จะไม่หลุดจากใย</em></h1>
          <p>เปลี่ยนสิ่งที่ต้องทำให้กลายเป็นภารกิจ สะสม XP รักษาสตรีก และเติบโตทีละก้าวในแบบฮีโร่ของคุณเอง</p>
          <div className="login-proof" aria-label="Product highlights">
            <span><strong>18</strong> ภารกิจสำเร็จ</span>
            <span><strong>12</strong> วันต่อเนื่อง</span>
            <span><strong>Lv.5</strong> พร้อมลุย</span>
          </div>
        </div>
        <div className="city-silhouette" aria-hidden="true">
          {[48, 76, 58, 92, 64, 108, 70, 86, 55, 102, 74, 62].map((height, index) => (
            <span key={index} style={{ height }} />
          ))}
        </div>
      </section>

      <section className="login-panel">
        <div className="login-card">
          <div className="mobile-login-logo"><Logo compact /></div>
          <div className="login-card-heading">
            <span className="section-index">01 / IDENTIFY</span>
            <h2>กลับสู่ศูนย์บัญชาการ</h2>
            <p>เข้าสู่ระบบเพื่อดูภารกิจและความก้าวหน้า</p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <label className="field-label" htmlFor="email">อีเมล</label>
            <div className="input-shell">
              <Mail size={19} aria-hidden="true" />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="hero@questup.app"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            <label className="field-label" htmlFor="password">รหัสผ่าน</label>
            <div className="input-shell">
              <LockKeyhole size={19} aria-hidden="true" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••••"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-describedby={error ? 'login-error' : undefined}
              />
              <button
                className="icon-button input-action"
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && <p className="form-error" id="login-error" role="alert">{error}</p>}

            <button className="primary-button login-submit" type="submit" disabled={loading}>
              {loading ? <><span className="spinner" /> กำลังตรวจสอบ...</> : <>เข้าสู่ระบบ <ArrowRight size={19} /></>}
            </button>
          </form>

          <div className="demo-access">
            <div>
              <span>บัญชีเดโมพร้อมใช้</span>
              <strong>demo@questup.app · questup123</strong>
            </div>
            <button className="secondary-button" type="button" onClick={enterDemo} disabled={loading}>
              เข้าเดโมทันที
            </button>
          </div>

          <p className="login-disclaimer"><Shield size={15} /> เดโมนี้เก็บข้อมูลไว้เฉพาะในเบราว์เซอร์ของคุณ</p>
        </div>
      </section>
    </main>
  );
}

function AppShell({ route, data, onNavigate, onAdd, onNotify, children }) {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, title: 'ใกล้เลเวลอัปแล้ว', detail: `อีก ${XP_PER_LEVEL - getLevelProgress(data.progress.totalXp)} XP ถึงเลเวล ${getLevel(data.progress.totalXp) + 1}` },
    { id: 2, title: 'สตรีกกำลังร้อนแรง', detail: `${data.progress.currentStreak} วันต่อเนื่อง อย่าปล่อยให้ขาดตอน` },
    { id: 3, title: 'ภารกิจเด่นรออยู่', detail: 'ตามหาสัญญาณปริศนาเหลืออีกเพียง 1 จุดตรวจ' },
  ]);
  const panelRef = useRef(null);

  useEffect(() => {
    const handleOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setNotificationsOpen(false);
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, []);

  const meta = PAGE_META[route] || PAGE_META['/dashboard'];
  const level = getLevel(data.progress.totalXp);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-top">
          <Logo />
          <div className="sidebar-level">
            <span className="mini-avatar"><SpiderMark size={24} /></span>
            <div>
              <strong>{data.profile.name}</strong>
              <span>LEVEL {String(level).padStart(2, '0')}</span>
            </div>
          </div>
          <nav className="sidebar-nav" aria-label="เมนูหลัก">
            <span className="nav-section-label">ศูนย์บัญชาการ</span>
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  type="button"
                  className={route === item.path ? 'is-active' : ''}
                  onClick={() => onNavigate(item.path)}
                  aria-current={route === item.path ? 'page' : undefined}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                  {route === item.path && <span className="nav-signal" />}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="sidebar-tip">
          <Sparkles size={19} />
          <div><strong>Daily Signal</strong><span>ทำภารกิจ 1 ข้อเพื่อรักษาสตรีก</span></div>
        </div>
      </aside>

      <div className="main-column">
        <header className="topbar">
          <div className="mobile-brand"><Logo compact /></div>
          <div className="page-heading">
            <span>{meta.eyebrow}</span>
            <h1>{meta.title}</h1>
            <p>{meta.description}</p>
          </div>
          <div className="topbar-actions">
            {route !== '/profile' && (
              <button className="primary-button topbar-add" type="button" onClick={onAdd}>
                <Plus size={19} /> <span>เพิ่มภารกิจ</span>
              </button>
            )}
            <div className="notification-wrap" ref={panelRef}>
              <button
                className="icon-button notification-button"
                type="button"
                onClick={() => setNotificationsOpen((open) => !open)}
                aria-label={`การแจ้งเตือน ${notifications.length} รายการ`}
                aria-expanded={notificationsOpen}
              >
                <Bell size={20} />
                {notifications.length > 0 && <span>{notifications.length}</span>}
              </button>
              {notificationsOpen && (
                <div className="notification-panel">
                  <div className="notification-heading">
                    <div><span>SPIDER-SENSE</span><strong>การแจ้งเตือน</strong></div>
                    <button className="icon-button" type="button" onClick={() => setNotificationsOpen(false)} aria-label="ปิดการแจ้งเตือน"><X size={18} /></button>
                  </div>
                  {notifications.length ? (
                    <div className="notification-list">
                      {notifications.map((item) => (
                        <div key={item.id} className="notification-item"><span className="signal-dot" /><div><strong>{item.title}</strong><p>{item.detail}</p></div></div>
                      ))}
                    </div>
                  ) : (
                    <div className="notification-empty"><CheckCircle2 size={28} /><span>อ่านครบทุกสัญญาณแล้ว</span></div>
                  )}
                  {notifications.length > 0 && (
                    <button
                      className="notification-clear"
                      type="button"
                      onClick={() => {
                        setNotifications([]);
                        onNotify('ทำเครื่องหมายว่าอ่านแล้วทั้งหมด', 'info');
                      }}
                    >
                      ทำเครื่องหมายว่าอ่านแล้ว
                    </button>
                  )}
                </div>
              )}
            </div>
            <button className="profile-shortcut" type="button" onClick={() => onNavigate('/profile')} aria-label="เปิดโปรไฟล์">
              <span><SpiderMark size={21} /></span>
              <div><strong>{data.profile.name}</strong><small>ฮีโร่ระดับ {level}</small></div>
              <ChevronRight size={17} />
            </button>
          </div>
        </header>

        <main className="content-area" key={route}>{children}</main>
      </div>

      <nav className="mobile-nav" aria-label="เมนูมือถือ">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              type="button"
              className={route === item.path ? 'is-active' : ''}
              onClick={() => onNavigate(item.path)}
              aria-current={route === item.path ? 'page' : undefined}
            >
              <Icon size={21} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function DashboardPage({ data, onNavigate, onAdd, onComplete, onOpen }) {
  const activeQuests = data.quests.filter((quest) => quest.status === 'active');
  const featured = activeQuests.find((quest) => quest.featured) || activeQuests[0];
  const level = getLevel(data.progress.totalXp);
  const levelXp = getLevelProgress(data.progress.totalXp);
  const unlockedCount = Object.keys(data.achievementUnlocks).length;

  const statCards = [
    { label: 'XP ทั้งหมด', value: data.progress.totalXp.toLocaleString(), suffix: 'XP', icon: Zap, tone: 'blue', note: `เลเวล ${level}` },
    { label: 'สตรีกปัจจุบัน', value: data.progress.currentStreak, suffix: 'วัน', icon: Flame, tone: 'red', note: `ดีที่สุด ${data.progress.bestStreak} วัน` },
    { label: 'ภารกิจสำเร็จ', value: data.progress.completedCount, suffix: 'ครั้ง', icon: CheckCircle2, tone: 'green', note: `${activeQuests.length} ภารกิจกำลังทำ` },
    { label: 'ตราที่ปลดล็อก', value: unlockedCount, suffix: `/ ${ACHIEVEMENTS.length}`, icon: Trophy, tone: 'amber', note: 'สะสมต่อไป' },
  ];

  return (
    <div className="dashboard-grid">
      <section className="hero-panel">
        <div className="hero-web" aria-hidden="true" />
        <div className="hero-copy">
          <span className="hero-kicker"><span className="live-dot" /> HERO STATUS · ACTIVE</span>
          <h2>พลังที่ยิ่งใหญ่<br />เริ่มจาก<span>ภารกิจเล็ก ๆ</span></h2>
          <p>คุณเดินหน้ามาแล้วอย่างยอดเยี่ยม อีกเพียง <strong>{XP_PER_LEVEL - levelXp} XP</strong> ก็จะขึ้นสู่เลเวล {level + 1}</p>
          <div className="hero-level-row">
            <div className="hero-level-badge"><small>LEVEL</small><strong>{String(level).padStart(2, '0')}</strong></div>
            <div className="hero-xp">
              <div><span>ความคืบหน้าเลเวล</span><strong>{levelXp} / {XP_PER_LEVEL} XP</strong></div>
              <ProgressBar value={levelXp} max={XP_PER_LEVEL} label={`ความคืบหน้าเลเวล ${levelXp} จาก ${XP_PER_LEVEL} XP`} />
            </div>
          </div>
        </div>
        <div className="hero-emblem" aria-hidden="true">
          <div className="hero-emblem-ring ring-one" />
          <div className="hero-emblem-ring ring-two" />
          <span><SpiderMark size={88} /></span>
          <small>Q-05</small>
        </div>
      </section>

      <section className="stats-grid" aria-label="สถิติผู้เล่น">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <article className={`stat-card tone-${stat.tone}`} key={stat.label}>
              <div className="stat-icon"><Icon size={22} /></div>
              <span>{stat.label}</span>
              <div className="stat-value"><strong>{stat.value}</strong><small>{stat.suffix}</small></div>
              <p>{stat.note}</p>
            </article>
          );
        })}
      </section>

      <section className="mission-section panel-section">
        <SectionHeading
          eyebrow="ACTIVE MISSIONS"
          title="ภารกิจที่กำลังดำเนินการ"
          actionLabel="ดูทั้งหมด"
          onAction={() => onNavigate('/quests')}
        />
        <div className="mission-list">
          {activeQuests.slice(0, 3).map((quest, index) => (
            <QuestRow
              key={quest.id}
              quest={quest}
              index={index + 1}
              onOpen={onOpen}
              onComplete={onComplete}
            />
          ))}
          {!activeQuests.length && <EmptyState onAction={onAdd} />}
        </div>
      </section>

      <aside className="dashboard-rail">
        {featured && (
          <section className="featured-card">
            <div className="featured-card-top">
              <span><Target size={18} /> ภารกิจเด่น</span>
              <small>+{featured.xpReward} XP</small>
            </div>
            <h3>{featured.title}</h3>
            <p>{featured.description}</p>
            <div className="featured-progress">
              <div><span>ความคืบหน้า</span><strong>{featured.progress}/{featured.total}</strong></div>
              <ProgressBar value={featured.progress} max={featured.total} tone="blue" label="ความคืบหน้าภารกิจเด่น" />
            </div>
            <button className="light-button" type="button" onClick={() => onOpen(featured.id)}>
              ดูรายละเอียด <ArrowRight size={18} />
            </button>
          </section>
        )}

        <section className="activity-card">
          <SectionHeading eyebrow="LATEST SIGNALS" title="ความเคลื่อนไหวล่าสุด" compact />
          <div className="activity-list">
            {data.activity.slice(0, 4).map((item) => <ActivityItem key={item.id} item={item} />)}
          </div>
        </section>

        <button className="quick-add-card" type="button" onClick={onAdd}>
          <span><Plus size={23} /></span>
          <div><strong>สร้างภารกิจใหม่</strong><small>จับเป้าหมายก่อนมันหลุดใย</small></div>
          <ChevronRight size={20} />
        </button>
      </aside>

      <section className="achievement-preview panel-section">
        <SectionHeading
          eyebrow="ACHIEVEMENT VAULT"
          title="ตราความสำเร็จ"
          actionLabel="เปิดคลังรางวัล"
          onAction={() => onNavigate('/achievements')}
        />
        <div className="achievement-mini-grid">
          {ACHIEVEMENTS.slice(0, 4).map((achievement) => (
            <AchievementCard key={achievement.id} achievement={achievement} data={data} compact />
          ))}
        </div>
      </section>
    </div>
  );
}

function SectionHeading({ eyebrow, title, actionLabel, onAction, compact = false }) {
  return (
    <div className={`section-heading ${compact ? 'is-compact' : ''}`}>
      <div><span>{eyebrow}</span><h2>{title}</h2></div>
      {actionLabel && (
        <button className="text-button" type="button" onClick={onAction}>{actionLabel}<ChevronRight size={17} /></button>
      )}
    </div>
  );
}

function QuestRow({ quest, index, onOpen, onComplete }) {
  const category = CATEGORY_META[quest.category];
  const difficulty = DIFFICULTY_META[quest.difficulty];
  const CategoryIcon = category.icon;
  return (
    <article className="quest-row">
      <button className="quest-row-main" type="button" onClick={() => onOpen(quest.id)} aria-label={`ดูรายละเอียด ${quest.title}`}>
        <span className="quest-number">{String(index).padStart(2, '0')}</span>
        <span className={`quest-category-icon tone-${category.color}`}><CategoryIcon size={20} /></span>
        <span className="quest-row-copy">
          <span className="quest-row-meta"><span>{category.label}</span><i /> <span className={`difficulty-text tone-${difficulty.color}`}>{difficulty.label}</span></span>
          <strong>{quest.title}</strong>
          <small><Clock3 size={14} /> {quest.dueDate === localDateKey() ? 'ครบกำหนดวันนี้' : `ครบกำหนด ${formatDate(quest.dueDate)}`}</small>
        </span>
        <span className="quest-row-xp"><Zap size={16} /> +{quest.xpReward} XP</span>
      </button>
      <button className="complete-button" type="button" onClick={() => onComplete(quest.id)}>
        <Circle size={18} /> <span>ทำสำเร็จ</span>
      </button>
    </article>
  );
}

function ActivityItem({ item }) {
  const config = {
    quest_completed: { icon: Check, tone: 'green', suffix: item.xpDelta ? `+${item.xpDelta} XP` : '' },
    quest_created: { icon: Plus, tone: 'blue', suffix: 'เพิ่มใหม่' },
    achievement: { icon: Trophy, tone: 'amber', suffix: 'ปลดล็อก' },
    streak: { icon: Flame, tone: 'red', suffix: 'สตรีก' },
  }[item.type] || { icon: Activity, tone: 'blue', suffix: '' };
  const Icon = config.icon;
  return (
    <div className="activity-item">
      <span className={`activity-icon tone-${config.tone}`}><Icon size={16} /></span>
      <div><strong>{item.title}</strong><small>{formatRelativeTime(item.createdAt)}</small></div>
      <span>{config.suffix}</span>
    </div>
  );
}

function QuestsPage({ data, onAdd, onEdit, onComplete, onArchive, onDelete, onOpen }) {
  const [statusFilter, setStatusFilter] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [query, setQuery] = useState('');

  const statusCounts = useMemo(() => ({
    active: data.quests.filter((quest) => quest.status === 'active').length,
    completed: data.quests.filter((quest) => quest.status === 'completed').length,
    archived: data.quests.filter((quest) => quest.status === 'archived').length,
  }), [data.quests]);

  const filteredQuests = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return data.quests.filter((quest) => {
      const statusMatch = quest.status === statusFilter;
      const categoryMatch = categoryFilter === 'all' || quest.category === categoryFilter;
      const searchMatch = !normalized || `${quest.title} ${quest.description}`.toLowerCase().includes(normalized);
      return statusMatch && categoryMatch && searchMatch;
    });
  }, [categoryFilter, data.quests, query, statusFilter]);

  const clearFilters = () => {
    setQuery('');
    setCategoryFilter('all');
    setStatusFilter('active');
  };

  return (
    <div className="quests-page">
      <section className="quest-toolbar">
        <div className="status-tabs" role="tablist" aria-label="สถานะภารกิจ">
          {[
            { id: 'active', label: 'กำลังทำ' },
            { id: 'completed', label: 'สำเร็จแล้ว' },
            { id: 'archived', label: 'คลัง' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={statusFilter === tab.id}
              className={statusFilter === tab.id ? 'is-active' : ''}
              onClick={() => setStatusFilter(tab.id)}
            >
              {tab.label}<span>{statusCounts[tab.id]}</span>
            </button>
          ))}
        </div>
        <div className="quest-filters">
          <label className="search-box">
            <span className="sr-only">ค้นหาภารกิจ</span>
            <Search size={18} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="ค้นหาภารกิจ..." />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="ล้างคำค้น"><X size={16} /></button>}
          </label>
          <label className="select-box">
            <SlidersHorizontal size={17} />
            <span className="sr-only">กรองประเภท</span>
            <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
              <option value="all">ทุกประเภท</option>
              {Object.entries(CATEGORY_META).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}
            </select>
          </label>
          <button className="primary-button toolbar-add" type="button" onClick={onAdd}><Plus size={18} /> เพิ่มภารกิจ</button>
        </div>
      </section>

      <section className="quest-board">
        <div className="quest-board-heading">
          <div><span>{statusFilter.toUpperCase()} QUESTS</span><h2>{statusFilter === 'active' ? 'ภารกิจที่พร้อมลุย' : statusFilter === 'completed' ? 'ภารกิจที่พิชิตแล้ว' : 'ภารกิจในคลัง'}</h2></div>
          <small>{filteredQuests.length} รายการ</small>
        </div>
        {filteredQuests.length ? (
          <div className="quest-card-grid">
            {filteredQuests.map((quest) => (
              <QuestCard
                key={quest.id}
                quest={quest}
                onOpen={onOpen}
                onEdit={onEdit}
                onComplete={onComplete}
                onArchive={onArchive}
                onDelete={onDelete}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title="ยังไม่มีภารกิจในมุมนี้"
            description="ลองเปลี่ยนตัวกรอง หรือติดภารกิจใหม่ลงบนบอร์ด"
            actionLabel={query || categoryFilter !== 'all' || statusFilter !== 'active' ? 'ล้างตัวกรอง' : 'เพิ่มภารกิจแรก'}
            onAction={query || categoryFilter !== 'all' || statusFilter !== 'active' ? clearFilters : onAdd}
          />
        )}
      </section>
    </div>
  );
}

function QuestCard({ quest, onOpen, onEdit, onComplete, onArchive, onDelete }) {
  const category = CATEGORY_META[quest.category];
  const difficulty = DIFFICULTY_META[quest.difficulty];
  const CategoryIcon = category.icon;
  const isActive = quest.status === 'active';
  const isCompleted = quest.status === 'completed';

  return (
    <article className={`quest-card status-${quest.status}`}>
      <div className="quest-card-accent" />
      <div className="quest-card-head">
        <span className={`quest-category-icon tone-${category.color}`}><CategoryIcon size={20} /></span>
        <div className="quest-badges">
          <span>{category.label}</span>
          <span className={`tone-${difficulty.color}`}>{difficulty.label}</span>
        </div>
        <span className="quest-card-xp"><Zap size={15} /> {quest.xpReward} XP</span>
      </div>
      <button className="quest-title-button" type="button" onClick={() => onOpen(quest.id)}>
        <h3>{quest.title}</h3>
      </button>
      <p>{quest.description || 'ภารกิจนี้กำลังรอรายละเอียดเพิ่มเติมจากฮีโร่'}</p>
      <div className="quest-card-info">
        <span><Clock3 size={15} /> {quest.dueDate ? (quest.dueDate === localDateKey() ? 'วันนี้' : formatDate(quest.dueDate)) : 'ไม่กำหนดเวลา'}</span>
        {isActive && quest.total > 1 && <span>{quest.progress}/{quest.total} ขั้นตอน</span>}
        {isCompleted && <span className="completed-label"><CheckCircle2 size={15} /> สำเร็จแล้ว</span>}
        {quest.status === 'archived' && <span><Archive size={15} /> อยู่ในคลัง</span>}
      </div>
      {isActive && quest.total > 1 && <ProgressBar value={quest.progress} max={quest.total} tone="blue" label="ความคืบหน้าภารกิจ" />}
      <div className="quest-card-actions">
        {isActive && (
          <button className="complete-card-button" type="button" onClick={() => onComplete(quest.id)}><Check size={18} /> ทำสำเร็จ</button>
        )}
        {isActive && <button className="icon-button" type="button" onClick={() => onEdit(quest)} aria-label={`แก้ไข ${quest.title}`} title="แก้ไข"><Pencil size={18} /></button>}
        {isCompleted && <button className="secondary-button archive-button" type="button" onClick={() => onArchive(quest.id)}><Archive size={17} /> เก็บเข้าคลัง</button>}
        <button className="icon-button danger-icon" type="button" onClick={() => onDelete(quest.id)} aria-label={`ลบ ${quest.title}`} title="ลบ"><Trash2 size={18} /></button>
      </div>
    </article>
  );
}

function AchievementsPage({ data }) {
  const unlocked = ACHIEVEMENTS.filter((item) => data.achievementUnlocks[item.id]).length;
  return (
    <div className="achievements-page">
      <section className="achievement-banner">
        <div className="achievement-banner-icon"><Trophy size={46} /></div>
        <div>
          <span>VAULT PROGRESS</span>
          <h2>{unlocked} จาก {ACHIEVEMENTS.length} ตรา ถูกปลดล็อกแล้ว</h2>
          <p>การเติบโตของฮีโร่ไม่ได้วัดจากการชนะครั้งเดียว แต่วัดจากการกลับมาลงมือทุกวัน</p>
        </div>
        <div className="achievement-banner-progress">
          <strong>{Math.round((unlocked / ACHIEVEMENTS.length) * 100)}%</strong>
          <ProgressBar value={unlocked} max={ACHIEVEMENTS.length} tone="amber" label="ความคืบหน้าตราความสำเร็จ" />
        </div>
      </section>
      <section className="achievement-full-grid">
        {ACHIEVEMENTS.map((achievement) => (
          <AchievementCard key={achievement.id} achievement={achievement} data={data} />
        ))}
      </section>
    </div>
  );
}

function AchievementCard({ achievement, data, compact = false }) {
  const Icon = achievement.icon;
  const unlockedAt = data.achievementUnlocks[achievement.id];
  const current = Math.min(achievement.value(data), achievement.target);
  const isUnlocked = Boolean(unlockedAt);
  return (
    <article className={`achievement-card ${compact ? 'is-compact' : ''} ${isUnlocked ? 'is-unlocked' : 'is-locked'} tone-${achievement.tone}`}>
      <div className="achievement-icon"><Icon size={compact ? 22 : 30} /></div>
      <div className="achievement-copy">
        <div className="achievement-status">{isUnlocked ? <><Check size={13} /> ปลดล็อกแล้ว</> : <><LockKeyhole size={13} /> ยังล็อกอยู่</>}</div>
        <h3>{achievement.title}</h3>
        {!compact && <p>{achievement.description}</p>}
        {isUnlocked ? (
          <small>ได้รับเมื่อ {formatDate(unlockedAt, { day: 'numeric', month: 'long', year: 'numeric' })}</small>
        ) : (
          <div className="achievement-progress">
            <div><span>ความคืบหน้า</span><strong>{current.toLocaleString()} / {achievement.target.toLocaleString()}</strong></div>
            <ProgressBar value={current} max={achievement.target} tone={achievement.tone} label={`ความคืบหน้า ${achievement.title}`} />
          </div>
        )}
      </div>
    </article>
  );
}

function ProfilePage({ data, onReset, onExport, onLogout }) {
  const level = getLevel(data.progress.totalXp);
  const activeCount = data.quests.filter((quest) => quest.status === 'active').length;
  const completedVisible = data.quests.filter((quest) => quest.status === 'completed').length;
  return (
    <div className="profile-page">
      <section className="profile-hero-card">
        <div className="profile-web" aria-hidden="true" />
        <div className="profile-avatar-large"><SpiderMark size={58} /><span>{level}</span></div>
        <div className="profile-identity">
          <span>QUESTUP HERO ID</span>
          <h2>{data.profile.name}</h2>
          <p><Mail size={16} /> {data.profile.email}</p>
          <small>เข้าร่วมเมื่อ {formatDate(data.profile.joinedAt, { day: 'numeric', month: 'long', year: 'numeric' })}</small>
        </div>
        <div className="profile-rank"><Shield size={25} /><div><small>อันดับปัจจุบัน</small><strong>WEB GUARDIAN</strong></div></div>
      </section>

      <section className="profile-stat-grid">
        <article><span><Zap size={20} /></span><div><small>XP สะสม</small><strong>{data.progress.totalXp.toLocaleString()}</strong></div></article>
        <article><span><Flame size={20} /></span><div><small>สตรีกดีที่สุด</small><strong>{data.progress.bestStreak} วัน</strong></div></article>
        <article><span><Target size={20} /></span><div><small>กำลังทำ</small><strong>{activeCount} ภารกิจ</strong></div></article>
        <article><span><CheckCircle2 size={20} /></span><div><small>สำเร็จบนบอร์ด</small><strong>{completedVisible} ภารกิจ</strong></div></article>
      </section>

      <section className="profile-settings-grid">
        <article className="settings-card">
          <div className="settings-card-head"><div className="settings-icon"><Download size={21} /></div><div><span>YOUR DATA</span><h3>สำรองข้อมูลภารกิจ</h3></div></div>
          <p>ดาวน์โหลดภารกิจ ความก้าวหน้า และกิจกรรมทั้งหมดเป็นไฟล์ JSON เก็บไว้กับคุณ</p>
          <button className="secondary-button" type="button" onClick={onExport}><Download size={17} /> ดาวน์โหลดข้อมูล</button>
        </article>
        <article className="settings-card danger-zone">
          <div className="settings-card-head"><div className="settings-icon"><RotateCcw size={21} /></div><div><span>RESET ZONE</span><h3>เริ่มข้อมูลเดโมใหม่</h3></div></div>
          <p>คืนค่าภารกิจ XP และ achievement ให้เหมือนครั้งแรกที่เปิดแอป</p>
          <button className="danger-button" type="button" onClick={onReset}><RotateCcw size={17} /> รีเซ็ตข้อมูลเดโม</button>
        </article>
      </section>

      <section className="logout-strip">
        <div><LogOut size={21} /><div><strong>ออกจากศูนย์บัญชาการ</strong><span>ข้อมูลภารกิจจะยังอยู่ในเบราว์เซอร์นี้</span></div></div>
        <button className="secondary-button" type="button" onClick={onLogout}>ออกจากระบบ</button>
      </section>
    </div>
  );
}

function NotFoundPage({ onNavigate }) {
  return (
    <section className="not-found">
      <div className="not-found-code">4<SpiderMark size={66} />4</div>
      <span>LOST IN THE MULTIVERSE</span>
      <h2>เส้นทางนี้ไม่อยู่บนแผนที่ภารกิจ</h2>
      <p>กลับสู่ศูนย์บัญชาการ แล้วเลือกภารกิจที่มีอยู่กันดีกว่า</p>
      <button className="primary-button" type="button" onClick={() => onNavigate('/dashboard')}><Home size={18} /> กลับหน้าภาพรวม</button>
    </section>
  );
}

function EmptyState({ title = 'บอร์ดยังว่างอยู่', description = 'เพิ่มภารกิจแรก แล้วเริ่มสะสม XP ได้เลย', actionLabel = 'เพิ่มภารกิจ', onAction }) {
  return (
    <div className="empty-state">
      <span><MapPinned size={30} /></span>
      <h3>{title}</h3>
      <p>{description}</p>
      <button className="secondary-button" type="button" onClick={onAction}><Plus size={17} /> {actionLabel}</button>
    </div>
  );
}

function useModalLifecycle(primaryRef, onClose) {
  useEffect(() => {
    const previousFocus = document.activeElement;
    document.body.classList.add('modal-open');
    primaryRef.current?.focus();
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', handleKey);
      previousFocus?.focus?.();
    };
  }, [onClose, primaryRef]);
}

function QuestEditorModal({ quest, onClose, onSave }) {
  const titleRef = useRef(null);
  const [form, setForm] = useState({
    title: quest?.title || '',
    description: quest?.description || '',
    category: quest?.category || 'daily',
    difficulty: quest?.difficulty || 'medium',
    dueDate: quest?.dueDate || localDateKey(),
  });
  const [error, setError] = useState('');
  useModalLifecycle(titleRef, onClose);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const submit = (event) => {
    event.preventDefault();
    const title = form.title.trim();
    if (title.length < 3) {
      setError('ชื่อภารกิจต้องมีอย่างน้อย 3 ตัวอักษร');
      titleRef.current?.focus();
      return;
    }
    onSave(form, quest?.id || null);
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card quest-editor" role="dialog" aria-modal="true" aria-labelledby="quest-editor-title">
        <div className="modal-head">
          <div><span>{quest ? 'EDIT MISSION' : 'NEW MISSION'}</span><h2 id="quest-editor-title">{quest ? 'แก้ไขภารกิจ' : 'ติดภารกิจลงบนบอร์ด'}</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="ปิด"><X size={20} /></button>
        </div>
        <form onSubmit={submit}>
          <label className="field-label" htmlFor="quest-title">ชื่อภารกิจ <span>{form.title.length}/80</span></label>
          <input
            ref={titleRef}
            id="quest-title"
            className="standalone-input"
            value={form.title}
            maxLength={80}
            onChange={(event) => { update('title', event.target.value); setError(''); }}
            placeholder="เช่น อ่านหนังสือให้จบ 1 บท"
            aria-describedby={error ? 'quest-error' : undefined}
          />
          {error && <p className="form-error" id="quest-error" role="alert">{error}</p>}

          <label className="field-label" htmlFor="quest-description">รายละเอียด <span>{form.description.length}/280</span></label>
          <textarea
            id="quest-description"
            className="standalone-input"
            rows="3"
            value={form.description}
            maxLength={280}
            onChange={(event) => update('description', event.target.value)}
            placeholder="บอกตัวเองให้ชัดว่าความสำเร็จหน้าตาเป็นอย่างไร"
          />

          <div className="form-grid-two">
            <label><span className="field-label">ประเภท</span><select className="standalone-input" value={form.category} onChange={(event) => update('category', event.target.value)}>{Object.entries(CATEGORY_META).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}</select></label>
            <label><span className="field-label">กำหนดเวลา</span><input className="standalone-input" type="date" value={form.dueDate} onChange={(event) => update('dueDate', event.target.value)} /></label>
          </div>

          <fieldset className="difficulty-fieldset">
            <legend className="field-label">ระดับความท้าทาย</legend>
            <div className="difficulty-options">
              {Object.entries(DIFFICULTY_META).map(([value, meta]) => (
                <label key={value} className={form.difficulty === value ? 'is-selected' : ''}>
                  <input type="radio" name="difficulty" value={value} checked={form.difficulty === value} onChange={(event) => update('difficulty', event.target.value)} />
                  <span className={`difficulty-dot tone-${meta.color}`} />
                  <span><strong>{meta.label}</strong><small>+{meta.xp} XP</small></span>
                  {form.difficulty === value && <Check size={17} />}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="modal-actions">
            <button className="secondary-button" type="button" onClick={onClose}>ยกเลิก</button>
            <button className="primary-button" type="submit"><Target size={18} /> {quest ? 'บันทึกการแก้ไข' : 'สร้างภารกิจ'}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function QuestDetailModal({ quest, onClose, onEdit, onComplete, onArchive }) {
  const closeRef = useRef(null);
  useModalLifecycle(closeRef, onClose);
  const category = CATEGORY_META[quest.category];
  const difficulty = DIFFICULTY_META[quest.difficulty];
  const CategoryIcon = category.icon;

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card quest-detail" role="dialog" aria-modal="true" aria-labelledby="quest-detail-title">
        <div className="detail-hero">
          <div className="detail-web" aria-hidden="true" />
          <button ref={closeRef} className="icon-button detail-close" type="button" onClick={onClose} aria-label="ปิด"><X size={20} /></button>
          <span className={`quest-category-icon tone-${category.color}`}><CategoryIcon size={24} /></span>
          <div className="quest-badges"><span>{category.label}</span><span className={`tone-${difficulty.color}`}>{difficulty.label}</span></div>
          <h2 id="quest-detail-title">{quest.title}</h2>
          <span className="detail-xp"><Zap size={17} /> รางวัล +{quest.xpReward} XP</span>
        </div>
        <div className="detail-body">
          <p>{quest.description || 'ยังไม่มีรายละเอียดสำหรับภารกิจนี้'}</p>
          <div className="detail-info-grid">
            <div><Clock3 size={18} /><span>กำหนดเวลา<strong>{quest.dueDate ? formatDate(quest.dueDate, { day: 'numeric', month: 'long', year: 'numeric' }) : 'ไม่กำหนด'}</strong></span></div>
            <div><Activity size={18} /><span>สถานะ<strong>{quest.status === 'active' ? 'กำลังดำเนินการ' : quest.status === 'completed' ? 'สำเร็จแล้ว' : 'เก็บในคลัง'}</strong></span></div>
          </div>
          {quest.total > 1 && (
            <div className="detail-progress"><div><span>ความคืบหน้า</span><strong>{quest.progress} / {quest.total} ขั้นตอน</strong></div><ProgressBar value={quest.progress} max={quest.total} tone="blue" label="ความคืบหน้าภารกิจ" /></div>
          )}
          <div className="modal-actions detail-actions">
            {quest.status === 'active' && <button className="secondary-button" type="button" onClick={() => onEdit(quest)}><Pencil size={17} /> แก้ไข</button>}
            {quest.status === 'active' && <button className="primary-button" type="button" onClick={() => onComplete(quest.id)}><Check size={18} /> ทำภารกิจสำเร็จ</button>}
            {quest.status === 'completed' && <button className="secondary-button" type="button" onClick={() => onArchive(quest.id)}><Archive size={17} /> เก็บเข้าคลัง</button>}
          </div>
        </div>
      </section>
    </div>
  );
}

function ConfirmDialog({ title, description, confirmLabel, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  useModalLifecycle(cancelRef, onCancel);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onCancel()}>
      <section className="modal-card confirm-card" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-description">
        <span className="confirm-icon"><Trash2 size={27} /></span>
        <h2 id="confirm-title">{title}</h2>
        <p id="confirm-description">{description}</p>
        <div className="modal-actions">
          <button ref={cancelRef} className="secondary-button" type="button" onClick={onCancel}>ยกเลิก</button>
          <button className="danger-button" type="button" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </section>
    </div>
  );
}

function Toast({ toast, onClose, onUndo }) {
  useEffect(() => {
    const timer = window.setTimeout(onClose, toast.action ? 6500 : 4200);
    return () => window.clearTimeout(timer);
  }, [onClose, toast.action]);
  const Icon = toast.type === 'warning' ? Shield : toast.type === 'info' ? Activity : CheckCircle2;
  return (
    <div className={`toast toast-${toast.type}`} role="status" aria-live="polite">
      <span className="toast-icon"><Icon size={19} /></span>
      <p>{toast.message}</p>
      {toast.action === 'undo' && <button type="button" onClick={onUndo}>ย้อนคืน</button>}
      <button className="icon-button" type="button" onClick={onClose} aria-label="ปิดข้อความ"><X size={17} /></button>
    </div>
  );
}

export default App;
